import { RfqTemplateType } from '@lune-fe/dapi-models'
import { Modal } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { NavigationSource } from '../Shared/BackButtonWithFromState'

import { Stage, StageContext } from './components/MultiStepFlow'
import { setRfqState } from './utils/rfqStateStore'
import { RFQState } from './utils/types'
import RFQ from './RFQ'

type RFQModalProps = {
    open: boolean
    onClose: (isSuccess: boolean) => void
    sheetType: RfqTemplateType
    from?: NavigationSource
}

const RFQModal = ({ sheetType, open, onClose, from }: RFQModalProps) => {
    const navigate = useNavigate()
    const [state, setState] = useState<RFQState | undefined>(undefined)
    const [modalMaxWidth, setModalMaxWidth] = useState<'sm' | 'md'>('sm')
    const handleModalSizeChange = useCallback((size: 'sm' | 'md') => setModalMaxWidth(size), [])
    // Use a ref to track the latest state value for use in navigate callback
    const stateRef = useRef<RFQState | undefined>(state)

    // Update ref when state changes
    useEffect(() => {
        stateRef.current = state
    }, [state])

    // Determine the route path based on sheetType
    const routePath =
        sheetType === 'rfq'
            ? '/calculate-emissions/tender'
            : '/calculate-emissions/logistics-sheets'

    const context: StageContext = {
        state,
        updateState: (newState: RFQState) => {
            stateRef.current = newState
            setState(newState)
        },
        navigate: (newStage: Stage) => {
            // When navigating to a mapping stage, navigate to the route with context
            // Use stateRef.current to get the latest state value
            if (
                (newStage === 'clientAccountDetails' || newStage === 'shippingDetails') &&
                stateRef.current
            ) {
                // Store state in shared store since React Router state can't serialize complex objects
                const stateKey = `rfq-state-${Date.now()}-${Math.random()}`
                setRfqState(stateKey, stateRef.current)
                navigate(routePath, {
                    state: {
                        initialStage: newStage,
                        stateKey,
                        from,
                    },
                })
                onClose(false) // Close modal as we're navigating away
            }
        },
        onSuccess: () => {
            navigate('/imports')
            onClose(true)
        },
    }

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setState(undefined)
            setModalMaxWidth('sm')
        }
    }, [open])

    const handleClose = () => {
        onClose(false)
    }
    if (!open) {
        return null
    }

    return (
        <Modal
            maxWidth={modalMaxWidth}
            fullWidth
            open={open}
            onClose={handleClose}
            sx={{
                '.modal-content': {
                    mt: 0,
                },
            }}
        >
            <Box sx={{ width: '100%' }}>
                <RFQ
                    sheetType={sheetType}
                    context={context}
                    onClose={onClose}
                    onModalSizeChange={handleModalSizeChange}
                />
            </Box>
        </Modal>
    )
}

export default RFQModal
